<script setup lang="ts">
  import type { IStateMachineRoutes } from '~/models/StateMachineModels'
  import type { PushToSaveScenarios } from '~/composables/PushToSave'
  import type { StateMachine } from '#components'

  const components = {
    signUp: defineAsyncComponent(() => import('~/components/pushToSave/PushToSaveSignUp.vue')),
    phoneCapture: defineAsyncComponent(
      () => import('~/components/pushToSave/PushToSavePhoneCapture.vue')
    ),
    signIn: defineAsyncComponent(() => import('~/components/pushToSave/PushToSaveSignIn.vue')),
    verify: defineAsyncComponent(() => import('~/components/pushToSave/PushToSaveVerify.vue')),
    retry: defineAsyncComponent(() => import('~/components/pushToSave/PushToSaveSignIn.vue')),
    successSignIn: defineAsyncComponent(
      () => import('~/components/pushToSave/PushToSaveSuccessSignIn.vue')
    ),
    successSignUp: defineAsyncComponent(
      () => import('~/components/pushToSave/PushToSaveSuccessSignUp.vue')
    )
  }

  export type PushToSaveComponent = keyof typeof components
  export interface PushToSaveMachine extends IStateMachineRoutes {
    initial: PushToSaveComponent
    components: Partial<{ [key in PushToSaveComponent]: { next: PushToSaveComponent } }>
  }

  const props = defineProps<{
    initial: PushToSaveComponent
    scenario: PushToSaveScenarios
    presentation: string
    skipPhoneCapture?: boolean
    source?: string | null | undefined
    formConfig?: Record<string, any> | undefined
    onComplete?: () => void
  }>()

  const emit = defineEmits<{ step: [state: PushToSaveComponent] }>()

  const routes = computed(
    () =>
      ({
        initial: props.initial,
        components: {
          retry: { next: 'verify' },
          signIn: { next: 'verify' },
          verify: { next: 'successSignIn' },
          signUp: { next: props.skipPhoneCapture ? 'successSignUp' : 'phoneCapture' },
          phoneCapture: { next: 'successSignUp' }
        }
      } as PushToSaveMachine)
  )

  const stateMachine = ref<InstanceType<typeof StateMachine> | null>(null)

  const currentState = computed(() => stateMachine.value?.current)

  watch(currentState, (state) => {
    if (state) emit('step', state as PushToSaveComponent)
    if (state === 'successSignUp' || state === 'successSignIn') {
      pushToSaveCompletedScenario.value = props.scenario
    }
  })

  defineExpose({
    currentState
  })
</script>

<template>
  <StateMachine
    ref="stateMachine"
    :id="`push-to-save-${scenario}-sm`"
    :components="components"
    :routes="routes"
    :config="scenario"
    :presentation="presentation"
    :initial-data="[
      { key: 'onComplete', value: props.onComplete },
      { key: 'source', value: props.source },
      { key: 'formConfig', value: props.formConfig },
      { key: 'skipPhoneCapture', value: props.skipPhoneCapture }
    ]"
  />
</template>

<style scoped></style>
